'use client';

/**
 * BooleanRenderer Component
 *
 * Renders BOOLEAN field type.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type NeonAccent, neonAccents } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface BooleanRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
  /** Use switch instead of checkbox */
  useSwitch?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledSwitchProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $size: 'small' | 'medium' | 'large';
}

const StyledSwitch = styled(
  Switch,
  transientOptions,
)<StyledSwitchProps>(({ theme, $isDark, $neonAccent, $size }) => {
  const accentColor = $neonAccent ? neonAccents[$neonAccent].base : theme.palette.primary.main;
  const glowColor = $neonAccent ? neonAccents[$neonAccent].glow : alpha(theme.palette.primary.main, 0.5);

  const sizes = {
    small: { width: 34, height: 20, thumb: 14, padding: 3 },
    medium: { width: 44, height: 24, thumb: 18, padding: 3 },
    large: { width: 52, height: 28, thumb: 22, padding: 3 },
  };

  const s = sizes[$size];

  return {
    width: s.width,
    height: s.height,
    padding: 0,

    '& .MuiSwitch-switchBase': {
      padding: s.padding,
      transition: transitionPresets.button,

      '&.Mui-checked': {
        transform: `translateX(${s.width - s.thumb - s.padding * 2}px)`,
        color: '#fff',

        '& + .MuiSwitch-track': {
          backgroundColor: accentColor,
          opacity: 1,
          border: 0,
          boxShadow: `0 0 12px ${glowColor}`,
        },

        '& .MuiSwitch-thumb': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        },
      },

      '&.Mui-disabled': {
        opacity: 0.5,
        '& + .MuiSwitch-track': {
          opacity: 0.3,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      width: s.thumb,
      height: s.thumb,
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      transition: transitionPresets.button,
    },

    '& .MuiSwitch-track': {
      borderRadius: s.height / 2,
      backgroundColor: $isDark ? alpha('#fff', 0.15) : alpha('#000', 0.2),
      opacity: 1,
      transition: transitionPresets.button,
    },

    '&:hover .MuiSwitch-track': {
      backgroundColor: $isDark ? alpha('#fff', 0.2) : alpha('#000', 0.25),
    },

    '&:hover .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: alpha(accentColor, 0.85),
    },
  };
});

interface StyledCheckboxProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $size: 'small' | 'medium' | 'large';
}

const StyledCheckbox = styled(
  Checkbox,
  transientOptions,
)<StyledCheckboxProps>(({ theme, $isDark, $neonAccent, $size }) => {
  const accentColor = $neonAccent ? neonAccents[$neonAccent].base : theme.palette.primary.main;
  const glowColor = $neonAccent ? neonAccents[$neonAccent].glow : alpha(theme.palette.primary.main, 0.5);

  const sizeMap = {
    small: '20px',
    medium: '24px',
    large: '28px',
  };

  return {
    padding: 8,
    transition: transitionPresets.button,

    '& .MuiSvgIcon-root': {
      fontSize: sizeMap[$size],
    },

    '&:hover': {
      backgroundColor: $isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
    },

    '&.Mui-checked': {
      color: accentColor,

      '&:hover': {
        backgroundColor: alpha(accentColor, 0.1),
      },
    },

    '&.Mui-focusVisible': {
      boxShadow: `0 0 0 3px ${glowColor}`,
    },

    '&.Mui-disabled': {
      opacity: 0.5,
    },
  };
});

const DisplayValue = styled(
  Box,
  transientOptions,
)<{
  $isDark: boolean;
  $checked: boolean;
  $neonAccent?: NeonAccent;
}>(({ $isDark, $checked, $neonAccent }) => {
  const accentColor = $neonAccent ? neonAccents[$neonAccent].base : '#10b981';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 12px',
    borderRadius: 8,
    backgroundColor: $checked
      ? alpha(accentColor, $isDark ? 0.15 : 0.1)
      : $isDark
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)',
    color: $checked ? accentColor : $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
  };
});

const BooleanDot = styled(
  Box,
  transientOptions,
)<{ $checked: boolean; $neonAccent?: NeonAccent }>(({ $checked, $neonAccent }) => {
  const accentColor = $neonAccent ? neonAccents[$neonAccent].base : '#10b981';

  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: $checked ? accentColor : 'rgba(128, 128, 128, 0.5)',
    boxShadow: $checked ? `0 0 8px ${accentColor}` : 'none',
  };
});

// ============================================================================
// Component
// ============================================================================

export const BooleanRenderer: React.FC<BooleanRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
  useSwitch = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const boolValue = Boolean(value);

  // Handle value change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.checked);
  };

  // Display mode
  if (mode === 'display') {
    return (
      <DisplayValue className={className} $isDark={isDark} $checked={boolValue} $neonAccent={neonAccent}>
        <BooleanDot $checked={boolValue} $neonAccent={neonAccent} />
        <Typography variant="body2" component="span">
          {boolValue ? 'Yes' : 'No'}
        </Typography>
      </DisplayValue>
    );
  }

  // Create the control element
  const controlElement = useSwitch ? (
    <StyledSwitch
      checked={boolValue}
      onChange={handleChange}
      disabled={disabled || field.disabled}
      $isDark={isDark}
      $neonAccent={neonAccent}
      $size={size}
    />
  ) : (
    <StyledCheckbox
      checked={boolValue}
      onChange={handleChange}
      disabled={disabled || field.disabled}
      $isDark={isDark}
      $neonAccent={neonAccent}
      $size={size}
    />
  );

  // Inline mode - just the control, no label
  if (mode === 'inline') {
    return (
      <Box className={className} sx={{ display: 'flex', alignItems: 'center' }}>
        {controlElement}
      </Box>
    );
  }

  // Edit mode - with label and helper text
  return (
    <Box className={className}>
      <FormControlLabel
        control={controlElement}
        label={field.label}
        disabled={disabled || field.disabled}
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '1.125rem' : '1rem',
            color: disabled ? 'text.disabled' : 'text.primary',
          },
        }}
      />
      {(error || field.helpText) && (
        <FormHelperText error={!!error} sx={{ ml: 4 }}>
          {error || field.helpText}
        </FormHelperText>
      )}
    </Box>
  );
};

BooleanRenderer.displayName = 'BooleanRenderer';

export default BooleanRenderer;
